import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { ActionsApi } from '@/Domain/api/Action/ApiAction/Actions.api';
import { ActionDto, AIActionUsageStatsDto } from '@/Domain/api/Action/dto/Action.dto';
import { ActionBox } from '@/Domain/components/Action/ActionBox/ActionBox';
import { RemoveActionModal } from '@/Domain/components/Action/RemoveAction.modal';
import { useUpdateActionDetails } from '@/Domain/hooks/Action/ApiAction/useUpdateActionDetails.hook';
import { useDuplicateAction } from '@/Domain/hooks/Action/useDuplicateAction.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

type Props = {
  action: ActionDto;
  isPreview?: boolean;
  stats?: AIActionUsageStatsDto;
};

export const ApiActionBox = ({ action, isPreview = false, stats }: Props) => {
  const { t } = useTranslation('actions_page');
  const queryClient = useQueryClient();
  const { toggleModal } = useModalStore();
  const { duplicateAction, isDuplicating, canDuplicate } = useDuplicateAction();

  const { handleUpdateDetails, updateActionErrorMessage } = useUpdateActionDetails();

  const handleFetchAction = async () =>
    await queryClient.fetchQuery({
      queryKey: [QueryKey.actions.httpRequest.get, action.id],
      queryFn: () => ActionsApi.get(action.id),
    });

  const handleActive = (isActive: boolean) => {
    // Fired from a click handler and never awaited: without this catch a rejected
    // toggle was an unhandled rejection, so the switch snapped back in silence.
    void handleUpdateDetails(action.id, { is_active: isActive }, true).catch((error) =>
      toast.error(updateActionErrorMessage(error)),
    );
  };

  const handleEdit = async () => {
    const httpRequestAction = await queryClient.ensureQueryData({
      queryKey: [QueryKey.actions.httpRequest.get, action.id],
    });
    toggleModal(MODAL_NAMES.HTTP_REQUEST_ACTION_SHEET, { httpRequestAction });
  };

  const handleDelete = () => {
    toggleModal(`${MODAL_NAMES.REMOVE_ACTION_MODAL_NAME}_${action.id}`);
  };

  return (
    <>
      <ActionBox
        dataTestId="http-ai-action"
        isPreview={isPreview}
        action={action}
        stats={stats}
        onMouseEnterAction={handleFetchAction}
        onActive={handleActive}
        onEdit={handleEdit}
        onDuplicate={() => duplicateAction(action.id)}
        isDuplicating={isDuplicating}
        canDuplicate={canDuplicate}
        onDelete={handleDelete}
        buttonLabel={t('button.edit_action')}
      />

      <RemoveActionModal actionId={action.id} />
    </>
  );
};
